import { datadogRum } from '@datadog/browser-rum';
import equals from 'ramda/es/equals';
import omit from 'ramda/es/omit';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import type React from 'react';
import SortOption from '~/constants/SortOption';
import type {
  AppliedBooleanFilters,
  AppliedFilters,
  AppliedListFilters,
  AppliedRangeFilters,
  FinanceFilter,
  FinanceType,
  MakeType,
  ModelType
} from '~/contracts';
import type { AvailabilityType } from '~/contracts';
import purgeEmpty from '~/utilities/purgeEmpty';

const getNextFilter = <T extends MakeType | ModelType | string>(
  listToCheck: T[],
  nextValue: T
): T[] => {
  return [...(listToCheck ?? []), nextValue];
};

type ModelOrString = ModelType | string;

const removeFromMakes = <T extends ModelOrString>(
  listToCheck: T[],
  name: string
): T[] => {
  const isTrim = typeof listToCheck[0] === 'string';

  return listToCheck?.filter(
    (filter: any) => (isTrim ? filter : filter.name) !== name
  );
};

type AvailabilityPayload = {
  value?: AvailabilityType;
};

type BooleanPayload = {
  filterKey: keyof AppliedBooleanFilters;
  value: boolean;
};

type ListPayload = {
  filterKey: keyof Omit<AppliedListFilters, 'makes'>;
  value: string;
};

export type ModelsKey = 'models' | 'parentModels';

type MakesPayload = {
  modelsKey?: ModelsKey;
  name: string;
  parents?: string[];
};

export type RangePayload = {
  filterKey: keyof AppliedRangeFilters;
  value: Partial<{ max: number; min: number }>;
};

export type FinancePayload = {
  filterKey: keyof FinanceFilter;
  value: Partial<FinanceType>;
};

export type AppliedFiltersContextValue = {
  appliedFilters: Partial<AppliedFilters>;
  clearAllFilters: () => void;
  pageNumber: number;
  setAppliedFilters: (filters: Partial<AppliedFilters>) => void;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setSortBy: React.Dispatch<React.SetStateAction<SortOption>>;
  sortBy: SortOption;
  updateAvailabilityFilter: (payload: AvailabilityPayload) => void;
  updateBooleanFilter: (payload: BooleanPayload) => void;
  updateListFilter: (payload: ListPayload) => void;
  updateMakes: (payload: MakesPayload) => void;
  updateRangeFilter: (payload: RangePayload | FinancePayload) => void;
};

const AppliedFiltersContext = createContext<AppliedFiltersContextValue>({
  appliedFilters: {},
  clearAllFilters() {},
  pageNumber: 1,
  setAppliedFilters() {},
  setPageNumber() {},
  setSortBy() {},
  sortBy: SortOption.MostPopular,
  updateAvailabilityFilter() {},
  updateBooleanFilter() {},
  updateListFilter() {},
  updateMakes() {},
  updateRangeFilter() {}
});

export type OwnProps = {
  filters?: AppliedFilters;
  pageNumber?: number;
  sortBy?: SortOption;
};

export function AppliedFiltersProvider(
  props: React.PropsWithChildren<OwnProps>
) {
  const [appliedFilters, setAppliedFiltersInternal] = useState<
    Partial<AppliedFilters>
  >(props.filters ?? {});
  const [pageNumber, setPageNumber] = useState(props.pageNumber ?? 1);
  const [sortBy, setSortBy] = useState(props.sortBy ?? SortOption.MostPopular);

  useEffect(() => {
    setAppliedFiltersInternal(props.filters ?? {});
  }, [props.filters]);

  const setAppliedFilters = useCallback<
    AppliedFiltersContextValue['setAppliedFilters']
  >((nextAppliedFilters: Partial<AppliedFilters>) => {
    setAppliedFiltersInternal(previousAppliedFilters =>
      equals(previousAppliedFilters, nextAppliedFilters)
        ? previousAppliedFilters
        : purgeEmpty(nextAppliedFilters)
    );
  }, []);

  const clearAllFilters = useCallback<
    AppliedFiltersContextValue['clearAllFilters']
  >(() => {
    setAppliedFiltersInternal({});
  }, []);

  const updateAvailabilityFilter = useCallback<
    AppliedFiltersContextValue['updateAvailabilityFilter']
  >(
    ({ value }: AvailabilityPayload) =>
      setAppliedFiltersInternal(currentFilters => {
        const { earliestDayToDeliveryOrPickup: ignored, ...otherFilters } =
          currentFilters;
        const nextFilter = value
          ? { earliestDayToDeliveryOrPickup: value }
          : {};

        return purgeEmpty({
          ...otherFilters,
          ...nextFilter
        });
      }),
    []
  );

  const updateBooleanFilter = useCallback<
    AppliedFiltersContextValue['updateBooleanFilter']
  >(
    ({ filterKey, value }: BooleanPayload) =>
      setAppliedFiltersInternal(currentFilters => {
        const { [filterKey]: ignored, ...otherFilters } = currentFilters;
        const nextFilter = value ? { [filterKey]: value } : {};

        return purgeEmpty({
          ...otherFilters,
          ...nextFilter
        });
      }),
    []
  );

  const updateListFilter = useCallback<
    AppliedFiltersContextValue['updateListFilter']
  >(
    ({ filterKey, value }: ListPayload) =>
      setAppliedFiltersInternal(currentFilters => {
        const otherFilters = omit([filterKey], currentFilters);

        const filter = currentFilters[filterKey] ?? [];
        const shouldRemove = filter.includes(value);

        const nextValues = shouldRemove
          ? filter.filter(f => f !== value)
          : [...(filter ?? []), value];

        const shouldResetTruckFacets =
          !!currentFilters?.bodyStyles?.length &&
          filterKey === 'bodyStyles' &&
          value === 'Pickup' &&
          ('towingCapacity' in otherFilters ||
            'truckCabTypes' in otherFilters ||
            'truckBedLengths' in otherFilters);

        const shouldRemoveFilter = nextValues.length === 0;
        const nextFilter = shouldRemoveFilter
          ? {}
          : { [filterKey]: nextValues };

        if (shouldResetTruckFacets) {
          return {
            ...omit(
              ['towingCapacity', 'truckCabTypes', 'truckBedLengths'],
              otherFilters
            ),
            ...nextFilter
          };
        }

        // Reset size classes if the last body style is being deselected
        const shouldResetSizeClasses =
          filterKey === 'bodyStyles' &&
          currentFilters?.bodyStyles?.length === 1 &&
          currentFilters?.bodyStyles?.includes(value);

        if (shouldResetSizeClasses) {
          return {
            ...omit(['sizeClasses'], otherFilters),
            ...nextFilter
          };
        }

        return purgeEmpty({
          ...otherFilters,
          ...nextFilter
        });
      }),
    []
  );

  const updateRangeFilter = useCallback<
    AppliedFiltersContextValue['updateRangeFilter']
  >(
    ({ filterKey, value }: RangePayload | FinancePayload) =>
      setAppliedFiltersInternal(currentFilters => {
        const { [filterKey]: ignored, ...otherFilters } = currentFilters;
        const shouldRemoveFilter = Object.keys(value).length === 0;
        const nextRange = shouldRemoveFilter
          ? {}
          : { [filterKey]: { ...value } };

        return purgeEmpty({
          ...otherFilters,
          ...nextRange
        });
      }),
    []
  );

  const updateMakes = useCallback<AppliedFiltersContextValue['updateMakes']>(
    ({ modelsKey, name, parents }: MakesPayload) =>
      setAppliedFiltersInternal(currentFilters => {
        const [maybeMake, maybeModel] = parents ?? [];
        const makeName = maybeMake ?? name;
        const modelName = maybeModel ?? name;

        // When user selects a parent model, all non-parent models need to be unselected
        const makes =
          modelsKey === 'parentModels'
            ? currentFilters.makes?.reduce<MakeType[]>(
                (acc, make) => acc.concat({ ...make, models: [] }),
                []
              )
            : currentFilters.makes;
        const makeToUpdate = makes?.find(make => make.name === makeName);

        // If `maybeModel` is passed in, and `modelsKey` is not,
        // then attempt to figure out which models array to update
        if (!modelsKey && maybeModel) {
          modelsKey = makeToUpdate?.parentModels?.some(
            model => model.name === maybeModel
          )
            ? 'parentModels'
            : 'models';
        }

        const modelsToUpdate = modelsKey ? makeToUpdate?.[modelsKey] ?? [] : [];
        const modelToUpdate = modelsToUpdate.find(
          model => model.name === modelName
        );

        let nextModels: ModelType[] = [];
        let nextTrims: string[] = [];

        const parentCount = parents?.length ?? 0;

        const isTrim = parentCount === 2 && modelToUpdate;
        if (isTrim) {
          const shouldRemoveTrim = modelToUpdate?.trims?.includes(name);

          nextTrims = shouldRemoveTrim
            ? removeFromMakes(modelToUpdate?.trims ?? [], name)
            : getNextFilter(modelToUpdate?.trims ?? [], name);

          nextModels = modelsKey
            ? [
                ...removeFromMakes(modelsToUpdate, modelName),
                { name: modelName, trims: nextTrims }
              ]
            : [];
        }

        const isModel = parentCount === 1 && makeToUpdate;
        if (isModel) {
          const shouldRemoveModel = modelsToUpdate.some(
            model => model?.name === name
          );

          nextModels = shouldRemoveModel
            ? removeFromMakes(modelsToUpdate, modelName)
            : getNextFilter(modelsToUpdate, {
                name: modelName,
                trims: nextTrims
              });
        }
        const shouldRemoveMake = makes?.some(make => make?.name === name);

        const nextMakes = removeFromMakes(makes ?? [], makeName);

        const nextFilter = shouldRemoveMake
          ? nextMakes
          : getNextFilter(
              nextMakes,
              modelsKey
                ? {
                    name: makeName,
                    [modelsKey]: nextModels
                  }
                : { name: makeName }
            );

        return purgeEmpty({ ...currentFilters, makes: nextFilter }) ?? {};
      }),
    []
  );

  useEffect(() => {
    if (appliedFilters.finance) {
      datadogRum.addAction('Finance Filter Applied', {});
    }
  }, [appliedFilters]);

  const value = useMemo<AppliedFiltersContextValue>(
    () => ({
      appliedFilters,
      clearAllFilters,
      pageNumber,
      setAppliedFilters,
      setPageNumber,
      setSortBy,
      sortBy,
      updateAvailabilityFilter,
      updateBooleanFilter,
      updateListFilter,
      updateMakes,
      updateRangeFilter
    }),
    [
      appliedFilters,
      clearAllFilters,
      pageNumber,
      setAppliedFilters,
      sortBy,
      updateAvailabilityFilter,
      updateBooleanFilter,
      updateListFilter,
      updateMakes,
      updateRangeFilter
    ]
  );

  return (
    <AppliedFiltersContext.Provider value={value}>
      {props.children}
    </AppliedFiltersContext.Provider>
  );
}

AppliedFiltersProvider.displayName = 'AppliedFiltersProvider';

export default AppliedFiltersContext;
